import { XMarkIcon } from './Icons';
import { useI18n } from '../../i18n';
import { useModalContext } from './modalContext';

interface ModalHeaderProps {
  /** The title, in Title Case. A string, or a string with an icon. */
  title: React.ReactNode;
  /** Optional subtitle line shown under the title: a name, a count, badges. */
  subtitle?: React.ReactNode;
  /** #552 — the close X is in every modal; a phone has no Esc key. Hide it
   *  only with a reason in a comment at the call site. */
  hideClose?: boolean;
}

/**
 * The header of every modal (#552): the title, the subtitle, then on the
 * right the close X. The record actions (Archive, Delete) are text buttons in
 * the footer (`ModalFooter` `actions`, #675). The X calls the `onClose` of
 * the ModalBackdrop around it.
 */
export default function ModalHeader({
  title, subtitle, hideClose,
}: ModalHeaderProps) {
  const { t } = useI18n();
  const modal = useModalContext();
  const close = modal && !hideClose ? (
    <button
      type="button"
      onClick={modal.onClose}
      title={t.common.close}
      aria-label={t.common.close}
      className="btn-icon shrink-0"
    >
      <XMarkIcon className="w-5 h-5" />
    </button>
  ) : null;

  const root = modal?.layout === 'panel'
    ? 'shrink-0 flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100'
    : 'flex items-start justify-between gap-3 mb-4';

  return (
    <div className={root}>
      <div className="min-w-0">
        <h2 className="font-bold text-lg leading-tight" data-testid="modal-title">{title}</h2>
        {subtitle && <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>}
      </div>
      {close && <div className="flex items-center gap-2 shrink-0">{close}</div>}
    </div>
  );
}
